import { useEffect, useRef, useState } from 'react';

import {
  ENDPOINTS,
  VENDOR_IMPORT_CONFLICT_REASONS,
  VENDOR_IMPORT_FILE_REFUSALS,
  VENDOR_IMPORT_PREAMBLE_TEXT_MAX,
  vendorImportTileOf,
  type ExecuteVendorImportRequest,
  type ExecuteVendorImportResponse,
  type ListVendorImportsResponse,
  type PlanVendorImportResponse,
  type VendorImportApprovedRow,
  type VendorImportConflictReason,
  type VendorImportFileRefusal,
  type VendorImportLimits,
  type VendorImportUnprocessableReason,
} from '@clm/shared-types';

import { ApiError } from '../api/errors';
import { classifyApiError, type ApiProblemKind } from '../api/problem';
import { refusalFieldError, sha256OfFile } from '../api/upload';
import { executeBodyBytes, executeVendorImport, listVendorImports, planVendorImport } from '../api/vendor-import';
import { loginRequest, msalInstance } from '../auth/msal';
import { CursorPager } from '../components/CursorPager';
import { ErrorNotice } from '../components/ErrorNotice';
import { useCloudeqEntities } from '../hooks/useCloudeqEntities';
import { requireSurface } from '../routes/surfaces';
import { PanelProblem } from './quality/panel';
import { usePaging, usePanel } from './quality/panel-state';
import {
  allComplete,
  approvedRows,
  includedRows,
  initialDecisions,
  orderedRows,
  withServerAsk,
  type RowDecision,
} from './vendor-import/decisions';
import { chooseSheetFile, isNotReadable } from './vendor-import/file-choice';
import { PlanTable } from './vendor-import/PlanTable';
import { ColumnsPanel, History, NotInSheet, PlanSummary, Receipt } from './vendor-import/pieces';
import {
  CONFLICT_COPY,
  CONFLICT_FOLLOW_UP,
  EXTENSION_REFUSAL_COPY,
  FILE_REFUSAL_COPY,
  SHEET_FLAG_COPY,
  TILE_COPY,
  TILE_ORDER,
  UNPROCESSABLE_COPY,
  entityName,
  isKeyOf,
  leadingToken,
  megabytes,
  rowLabel,
  tokenDetail,
  type VendorImportTile,
} from './vendorImportCopy';

/**
 * Admin **Vendor list** — CLM-113 + CLM-114 (FR-39, FR-40, FR-40a, DR-12,
 * NFR-19).
 *
 * Gerry: *"give me an interface to upload the XLS with vendor name, id and
 * addresses, so we can load that in the system and then identify contracts
 * against it."* An Admin chooses the Business Central "Vendor" export (`.xlsx`)
 * or a CSV of it, reads the per-row **plan** the API returns, decides the rows
 * that need a person, and **imports** the rows they approve. The CLM-57 shape
 * (`UploadBundleReview.tsx`): nothing is written before execute, the same
 * `File` is re-sent with the plan's digest, and any change in between is a
 * `409` this page answers by reading the sheet again.
 *
 * ## The API is the boundary
 *
 * Every call is gated `party:import_vendors` (Admin only, R-113-20). The rail
 * entry is hidden from a non-admin as a courtesy, but the route stays
 * registered and a non-admin who types the path gets the `403` notice from the
 * history read. Nothing here is disabled by role.
 *
 * ## Decisions are never carried over
 *
 * A fresh plan always gets fresh decisions (`initialDecisions`): on a `409
 * file_changed` / `plan_changed` the page plans again at once and says, in a
 * neutral banner, that every decision has been cleared — a decision is never
 * carried onto a row whose plan may have changed (§4.7).
 *
 * ## No browser storage
 *
 * The plan holds vendor names and addresses. It lives in this component's
 * state only, never in `localStorage` / `sessionStorage`, and leaving the page
 * drops it.
 *
 * ## Bounds (NFR-19, R-113-21)
 *
 * `limits` is read on load from the history call, so an oversized file is
 * refused before it is sent (the server's `413` carries no field error, so the
 * page names the bound itself). Before execute the page measures the body —
 * the file plus the approved list — against the request cap and says so
 * instead of sending a request the server would refuse unreadably.
 */

type Phase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'reading' }
  | { readonly kind: 'planning' }
  | { readonly kind: 'planned'; readonly plan: PlanVendorImportResponse }
  | { readonly kind: 'executing'; readonly plan: PlanVendorImportResponse; readonly count: number }
  | { readonly kind: 'executed'; readonly plan: PlanVendorImportResponse; readonly receipt: ExecuteVendorImportResponse };

type Problem =
  | { readonly kind: 'file_refusal'; readonly token: VendorImportFileRefusal; readonly detail: string | null }
  | { readonly kind: 'over_limit' }
  | { readonly kind: 'not_readable' }
  | { readonly kind: 'descriptor_mismatch' }
  | { readonly kind: 'body_too_large'; readonly bytes: number; readonly limit: number; readonly rows: number }
  | {
      readonly kind: 'unprocessable';
      readonly token: VendorImportUnprocessableReason | null;
      readonly message: string;
      readonly row_ref: string | null;
    }
  | { readonly kind: 'api'; readonly problem: ApiProblemKind; readonly detail: string | null; readonly correlation_id: string | null };

const FILE_REFUSALS: ReadonlySet<string> = new Set(VENDOR_IMPORT_FILE_REFUSALS);
const CONFLICT_REASONS: ReadonlySet<string> = new Set(VENDOR_IMPORT_CONFLICT_REASONS);
const FORBIDDEN_COPY =
  'Importing the vendor list is an Admin action (party:import_vendors). The page stays here, but every call to it is refused.';

/** `approved[3].action` or `approved.3.action` → 3. */
function approvedIndex(path: string): number | null {
  const match = /^approved(?:\[(\d+)\]|\.(\d+))/.exec(path);
  if (match === null) return null;
  return Number(match[1] ?? match[2]);
}

function conflictReasonOf(error: unknown): VendorImportConflictReason | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const field = refusalFieldError(error.body);
  const token = field === null ? '' : leadingToken(field.message);
  return CONFLICT_REASONS.has(token) ? (token as VendorImportConflictReason) : 'plan_changed';
}

function problemOf(error: unknown, approved: readonly VendorImportApprovedRow[]): Problem {
  if (isNotReadable(error)) return { kind: 'not_readable' };
  if (error instanceof ApiError) {
    if (error.status === 413) return { kind: 'over_limit' };
    const field = refusalFieldError(error.body);
    if (field !== null && (error.status === 400 || error.status === 422)) {
      const token = leadingToken(field.message);
      if (field.path === 'file' && FILE_REFUSALS.has(token)) {
        return { kind: 'file_refusal', token: token as VendorImportFileRefusal, detail: tokenDetail(field.message) };
      }
      if (field.path === 'file.sha256' || field.path === 'file.size_bytes') return { kind: 'descriptor_mismatch' };
      if (error.status === 422) {
        const index = approvedIndex(field.path);
        const known = isKeyOf(UNPROCESSABLE_COPY, token) ? token : null;
        return {
          kind: 'unprocessable',
          token: known,
          message: known === null ? field.message : UNPROCESSABLE_COPY[known],
          row_ref: index === null ? null : (approved[index]?.row_ref ?? null),
        };
      }
    }
    return { kind: 'api', problem: classifyApiError(error), detail: error.message, correlation_id: error.correlation_id };
  }
  return { kind: 'api', problem: classifyApiError(error), detail: null, correlation_id: null };
}

function ProblemNotice({ problem, limits }: { readonly problem: Problem; readonly limits: VendorImportLimits | null }) {
  switch (problem.kind) {
    case 'file_refusal':
      return (
        <div className="banner bad" role="alert">
          <p className="notice-head">
            <strong>The file was refused</strong>
          </p>
          <p>{FILE_REFUSAL_COPY[problem.token]}</p>
          {problem.detail === null ? null : <p className="mut">The API said: {problem.detail}</p>}
        </div>
      );
    case 'over_limit':
      return (
        <div className="banner bad" role="alert">
          <p className="notice-head">
            <strong>
              {limits === null ? 'The file is over the import limit' : `The file is over the import limit of ${megabytes(limits.max_bytes)}`}
            </strong>
          </p>
          <p>Nothing was read. Split the vendor list, or ask for the import limit to be raised.</p>
        </div>
      );
    case 'not_readable':
      return (
        <div className="banner bad" role="alert">
          <p className="notice-head">
            <strong>The file changed on disk; choose it again.</strong>
          </p>
          <p>Nothing was sent. The page cannot re-send the bytes this plan was read from.</p>
        </div>
      );
    case 'descriptor_mismatch':
      return (
        <div className="banner bad" role="alert">
          <p className="notice-head">
            <strong>The file changed while it was being sent; choose it again.</strong>
          </p>
          <p>Nothing was written.</p>
        </div>
      );
    case 'body_too_large':
      return (
        <div className="banner bad" role="alert">
          <p className="notice-head">
            <strong>This import is too large to send in one request</strong>
          </p>
          <p>
            The file and the {problem.rows} approved rows come to {megabytes(problem.bytes)}, over the request limit of{' '}
            {megabytes(problem.limit)}. Nothing was sent. Import fewer rows at a time.
          </p>
        </div>
      );
    case 'unprocessable':
      return (
        <div className="banner bad" role="alert">
          <p className="notice-head">
            <strong>
              {problem.row_ref === null
                ? 'The import was refused; nothing was written'
                : `${rowLabel(problem.row_ref)} could not be imported as chosen; nothing was written`}
            </strong>
          </p>
          <p>{problem.message}</p>
        </div>
      );
    case 'api':
    default:
      return <ErrorNotice kind={problem.problem} detail={problem.detail} correlation_id={problem.correlation_id} />;
  }
}

export function VendorImport() {
  const surface = requireSurface('vendor-import');
  const entities = useCloudeqEntities();
  const paging = usePaging();
  const { state: history, reload: reloadHistory } = usePanel<ListVendorImportsResponse>(
    (signal) => listVendorImports(paging.cursor === undefined ? {} : { page: { cursor: paging.cursor } }, signal),
    [paging.cursor],
  );

  const [file, setFile] = useState<File | null>(null);
  const [choiceError, setChoiceError] = useState<string | null>(null);
  const [pickedEntity, setPickedEntity] = useState('');
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [decisions, setDecisions] = useState<Record<string, RowDecision>>({});
  const [problem, setProblem] = useState<Problem | null>(null);
  const [notice, setNotice] = useState<VendorImportConflictReason | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState<VendorImportTile | 'all'>('all');
  const [confirmWeaker, setConfirmWeaker] = useState(false);
  const [planLimits, setPlanLimits] = useState<VendorImportLimits | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const receiptRef = useRef<HTMLHeadingElement | null>(null);

  const limits: VendorImportLimits | null = planLimits ?? (history.kind === 'ready' ? history.response.limits : null);
  const busy = phase.kind === 'reading' || phase.kind === 'planning' || phase.kind === 'executing';
  const plan =
    phase.kind === 'planned' || phase.kind === 'executing' || phase.kind === 'executed' ? phase.plan : null;
  const activeEntities = [...entities].filter((entity) => entity.is_active).sort((a, b) => a.sort_order - b.sort_order);

  useEffect(() => {
    if (phase.kind === 'executed') receiptRef.current?.focus();
  }, [phase]);

  useEffect(() => () => abortRef.current?.abort(), []);

  function start(work: (controller: AbortController) => Promise<void>): void {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    void work(controller);
  }

  function reset(): void {
    abortRef.current?.abort();
    setPhase({ kind: 'idle' });
    setDecisions({});
    setProblem(null);
    setNotice(null);
    setRowErrors({});
    setFilter('all');
    setConfirmWeaker(false);
    setPickedEntity('');
  }

  function choose(candidate: File | null): void {
    reset();
    if (candidate === null) {
      setFile(null);
      setChoiceError(null);
      return;
    }
    const choice = chooseSheetFile(candidate, limits?.max_bytes ?? null);
    if (choice.kind === 'refused_extension') {
      setChoiceError(EXTENSION_REFUSAL_COPY[choice.refusal]);
      setFile(null);
      return;
    }
    if (choice.kind === 'refused_size') {
      setChoiceError(
        `The file is ${megabytes(candidate.size)}, over the import limit of ${megabytes(choice.max_bytes)}. Nothing was sent. Split the vendor list, or ask for the limit to be raised.`,
      );
      setFile(null);
      return;
    }
    setChoiceError(null);
    setFile(candidate);
  }

  function refuse(error: unknown, approved: readonly VendorImportApprovedRow[]): Problem | null {
    if (error instanceof ApiError && error.isUnauthenticated) {
      void msalInstance.loginRedirect(loginRequest);
      return null;
    }
    return problemOf(error, approved);
  }

  /**
   * Plan the sheet. `restore` is the plan on screen when the Admin re-plans
   * under a picked company: a refusal (a `422 entity_unknown` when the company
   * was deactivated after the page loaded) then puts that plan, and its company
   * select, back beside the message instead of emptying the page.
   */
  async function runPlan(
    sheet: File,
    entityKey: string | null,
    replanned: VendorImportConflictReason | null,
    controller: AbortController,
    restore: PlanVendorImportResponse | null = null,
  ): Promise<void> {
    const choice = chooseSheetFile(sheet, null);
    if (choice.kind !== 'accepted') return;
    try {
      setProblem(null);
      setPhase({ kind: 'reading' });
      const sha256 = await sha256OfFile(sheet);
      if (controller.signal.aborted) return;
      setPhase({ kind: 'planning' });
      const next = await planVendorImport(
        {
          file: { file_name: sheet.name, media_type: choice.media_type, size_bytes: sheet.size, sha256 },
          ...(entityKey === null ? {} : { cloudeq_entity_key: entityKey }),
        },
        sheet,
        controller.signal,
      );
      if (controller.signal.aborted) return;
      setPlanLimits(next.limits);
      setDecisions(initialDecisions(next));
      setRowErrors({});
      setFilter('all');
      setConfirmWeaker(false);
      setNotice(replanned);
      setPhase({ kind: 'planned', plan: next });
    } catch (error) {
      if (controller.signal.aborted) return;
      setPhase(restore === null ? { kind: 'idle' } : { kind: 'planned', plan: restore });
      setNotice(null);
      setProblem(refuse(error, []));
    }
  }

  async function runExecute(sheet: File, current: PlanVendorImportResponse, controller: AbortController): Promise<void> {
    const choice = chooseSheetFile(sheet, null);
    const entityKey = current.entity.chosen_key;
    const digest = current.plan_digest;
    if (choice.kind !== 'accepted' || entityKey === null || digest === null) return;
    const approved = approvedRows(current, decisions);
    setProblem(null);
    setNotice(null);
    setRowErrors({});
    setPhase({ kind: 'executing', plan: current, count: approved.length });
    try {
      /* Re-read the same File: a NotReadableError means the bytes on disk changed or moved, and nothing is sent. */
      const sha256 = await sha256OfFile(sheet);
      if (controller.signal.aborted) return;
      const request: ExecuteVendorImportRequest = {
        file: { file_name: sheet.name, media_type: choice.media_type, size_bytes: sheet.size, sha256 },
        planned_sha256: current.file.sha256,
        cloudeq_entity_key: entityKey,
        plan_digest: digest,
        ...(current.sheet_flags.includes('individual_test_weaker') ? { confirm_individual_test_weaker: confirmWeaker } : {}),
        approved,
      };
      const bytes = executeBodyBytes(request, sheet);
      const cap = limits?.max_request_bytes ?? null;
      if (cap !== null && bytes > cap) {
        setPhase({ kind: 'planned', plan: current });
        setProblem({ kind: 'body_too_large', bytes, limit: cap, rows: approved.length });
        return;
      }
      const receipt = await executeVendorImport(request, sheet, controller.signal);
      if (controller.signal.aborted) return;
      setPhase({ kind: 'executed', plan: current, receipt });
      /* Back to the newest page, so the batch just written is listed. */
      paging.reset();
      reloadHistory();
    } catch (error) {
      if (controller.signal.aborted) return;
      const conflict = conflictReasonOf(error);
      if (conflict !== null) {
        /* The file or the registry moved: plan again at once, with every decision cleared (§4.7). */
        await runPlan(sheet, current.entity.detected_key === null ? entityKey : null, conflict, controller);
        return;
      }
      setPhase({ kind: 'planned', plan: current });
      const next = refuse(error, approved);
      if (next?.kind === 'unprocessable' && next.row_ref !== null) {
        const rowRef = next.row_ref;
        setRowErrors({ [rowRef]: next.message });
        /* A confirmation the plan could not foresee: show its control on that row, so the Admin can answer it. */
        const ask =
          next.token === 'shared_alias_unconfirmed'
            ? ('accept_shared_aliases' as const)
            : next.token === 'short_name_unconfirmed'
              ? ('confirm_short_names' as const)
              : null;
        if (ask !== null) {
          setDecisions((decided) => {
            const decision = decided[rowRef];
            return decision === undefined ? decided : { ...decided, [rowRef]: withServerAsk(decision, ask) };
          });
        }
      }
      setProblem(next);
    }
  }

  /* ---- derived ----------------------------------------------------------- */

  const ticked = plan === null ? [] : includedRows(plan, decisions);
  const complete = plan === null ? false : allComplete(plan, decisions);
  const weaker = plan?.sheet_flags.includes('individual_test_weaker') === true;
  const chosenName = plan === null ? '' : entityName(entities, plan.entity.chosen_key);
  const canExecute =
    plan !== null &&
    file !== null &&
    phase.kind === 'planned' &&
    !plan.entity.required &&
    ticked.length > 0 &&
    complete &&
    (!weaker || confirmWeaker);
  const shownRows =
    plan === null
      ? []
      : orderedRows(filter === 'all' ? plan.rows : plan.rows.filter((row) => vendorImportTileOf(row) === filter));
  const blockedAfterImport = plan === null ? 0 : plan.rows.filter((row) => row.flags.includes('privacy_blocked_after_import')).length;

  /* ---- render ------------------------------------------------------------ */

  return (
    <>
      <div className="hd">
        <div>
          <h2 id="vendor-import-heading">{surface.label}</h2>
          <p>{surface.summary}</p>
          <div className="refs">
            {surface.requirements.map((requirement) => (
              <span key={requirement}>{requirement}</span>
            ))}
          </div>
        </div>
      </div>

      <section className="card vendor-import" aria-labelledby="vendor-import-heading" aria-busy={busy}>
        <h3>Read a vendor list</h3>
        <p>
          Choose the Business Central “Vendor” export, or a CSV of it. CLM reads the vendor number, the names and the
          address of each row — never contact details, tax ids or anything else — and shows you what importing each
          row would do. Nothing is written until you import the rows you approve.
        </p>

        <div className="frow">
          <div className="field grow">
            <label htmlFor="vendor-import-file">Vendor list (.xlsx or .csv)</label>
            <input
              id="vendor-import-file"
              type="file"
              accept=".xlsx,.csv"
              disabled={busy}
              aria-describedby="vendor-import-accepts"
              onChange={(event) => choose(event.target.files?.[0] ?? null)}
            />
            <p id="vendor-import-accepts" className="mut">
              An Excel workbook (.xlsx) or a CSV saved as “CSV UTF-8”. Only the first sheet is read.
              {limits === null ? '' : ` Up to ${megabytes(limits.max_bytes)} and ${limits.max_rows.toLocaleString('en-GB')} rows.`}
            </p>
          </div>
        </div>

        {choiceError === null ? null : (
          <p className="ferr" role="alert">
            {choiceError}
          </p>
        )}

        <div className="bundle-actions">
          <button
            className="btn"
            type="button"
            disabled={busy || file === null}
            onClick={() => {
              if (file !== null) start((controller) => runPlan(file, null, null, controller));
            }}
          >
            {phase.kind === 'reading' ? 'Reading the file…' : phase.kind === 'planning' ? 'Planning…' : 'Read this sheet'}
          </button>
        </div>

        {busy ? (
          <p role="status" aria-busy="true" className="mut">
            {phase.kind === 'executing' ? `Importing ${String(phase.count)} rows…` : 'Reading the sheet…'}
          </p>
        ) : null}

        {notice === null ? null : (
          <div className="banner up" role="status" data-testid="vi-replanned">
            <p className="notice-head">
              <strong>{CONFLICT_COPY[notice]}</strong>
            </p>
            <p>{CONFLICT_FOLLOW_UP}</p>
          </div>
        )}

        {problem === null ? null : <ProblemNotice problem={problem} limits={limits} />}

        {plan !== null ? (
          <>
            <EntityLine
              plan={plan}
              entityLabel={chosenName}
              activeEntities={activeEntities}
              picked={pickedEntity}
              disabled={busy || phase.kind === 'executed'}
              onPick={setPickedEntity}
              onPlan={(key) => {
                const shown = plan;
                if (file !== null) start((controller) => runPlan(file, key, null, controller, shown));
              }}
            />

            {plan.entity.required ? null : (
              <>
                <PlanSummary plan={plan} />
                <ColumnsPanel plan={plan} />
                <NotInSheet plan={plan} />

                {blockedAfterImport > 0 ? (
                  <div className="banner up" role="status">
                    <p className="notice-head">
                      <strong>
                        {blockedAfterImport} {blockedAfterImport === 1 ? 'vendor' : 'vendors'} CLM already holds{' '}
                        {blockedAfterImport === 1 ? 'is' : 'are'} now Privacy Blocked in the source
                      </strong>
                    </p>
                    <p>They are listed first. Only a person can act on what CLM already holds about them.</p>
                  </div>
                ) : null}

                {phase.kind === 'executed' ? (
                  <Receipt ref={receiptRef} receipt={phase.receipt} plan={plan} entities={entities} />
                ) : null}

                <div className="frow vi-filter">
                  <div className="field">
                    <label htmlFor="vendor-import-filter">Show</label>
                    <select
                      id="vendor-import-filter"
                      value={filter}
                      onChange={(event) => setFilter(event.target.value as VendorImportTile | 'all')}
                    >
                      <option value="all">All rows ({plan.rows.length})</option>
                      {TILE_ORDER.map((tile) => (
                        <option key={tile} value={tile}>
                          {TILE_COPY[tile]} ({plan.totals[tile]})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <PlanTable
                  rows={shownRows}
                  decisions={decisions}
                  readOnly={phase.kind !== 'planned'}
                  busy={phase.kind === 'executing'}
                  rowErrors={rowErrors}
                  onDecide={(rowRef, next) => {
                    setRowErrors((current) => {
                      if (current[rowRef] === undefined) return current;
                      const rest = { ...current };
                      delete rest[rowRef];
                      return rest;
                    });
                    setDecisions((current) => ({ ...current, [rowRef]: next }));
                  }}
                  empty={<p>{filter === 'all' ? 'The sheet has no data rows.' : 'No row of this kind.'}</p>}
                />

                {phase.kind === 'planned' || phase.kind === 'executing' ? (
                  <div className="vi-execute">
                    {weaker ? (
                      <div className="banner up" role="status">
                        <p>{SHEET_FLAG_COPY.individual_test_weaker}</p>
                        <label className="vi-check">
                          <input
                            type="checkbox"
                            checked={confirmWeaker}
                            disabled={phase.kind !== 'planned'}
                            onChange={(event) => setConfirmWeaker(event.target.checked)}
                          />{' '}
                          I have checked this sheet for individuals
                        </label>
                      </div>
                    ) : null}
                    {ticked.length > 0 && !complete ? (
                      <p className="mut" role="note">
                        Some ticked rows still need something (marked ⚠). Import is enabled once each one is complete.
                      </p>
                    ) : null}
                    <p className="mut">
                      Adding a name to a vendor that already has contracts changes those contracts’ pseudonymisation
                      map; the receipt says whether that happened and what an operator runs next.
                    </p>
                    <button
                      className="btn pri"
                      type="button"
                      disabled={!canExecute}
                      onClick={() => {
                        if (file !== null && phase.kind === 'planned') {
                          const current = phase.plan;
                          start((controller) => runExecute(file, current, controller));
                        }
                      }}
                    >
                      {phase.kind === 'executing'
                        ? 'Importing…'
                        : `Import ${String(ticked.length)} ${ticked.length === 1 ? 'row' : 'rows'} under ${chosenName}`}
                    </button>
                  </div>
                ) : null}

                {phase.kind === 'executed' && file !== null ? (
                  <div className="bundle-actions">
                    <button
                      className="btn"
                      type="button"
                      onClick={() =>
                        start((controller) =>
                          runPlan(file, plan.entity.detected_key === null ? plan.entity.chosen_key : null, null, controller),
                        )
                      }
                    >
                      Read the sheet again
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </>
        ) : null}
      </section>

      <section className="card" aria-labelledby="vendor-import-history-heading">
        <h3 id="vendor-import-history-heading">Imports so far</h3>
        {history.kind === 'loading' ? (
          <p role="status" aria-busy="true">
            Reading the import history…
          </p>
        ) : null}
        {history.kind === 'problem' ? (
          <PanelProblem
            panel="the import history"
            state={history}
            endpointPath={ENDPOINTS['list-vendor-imports'].path}
            onRetry={reloadHistory}
            forbiddenCopy={FORBIDDEN_COPY}
          />
        ) : null}
        {history.kind === 'ready' ? (
          <>
            <History batches={history.response.batches} entities={entities} />
            {history.response.page.next_cursor !== null || paging.historyDepth > 0 ? (
              <CursorPager
                next_cursor={history.response.page.next_cursor}
                historyDepth={paging.historyDepth}
                pageNumber={paging.page}
                returnedCount={history.response.page.returned_count}
                totalInScope={history.response.page.total_in_scope}
                onNext={() => {
                  const next = history.response.page.next_cursor;
                  if (next !== null) paging.next(next);
                }}
                onPrevious={paging.previous}
              />
            ) : null}
          </>
        ) : null}
      </section>
    </>
  );
}

/**
 * The cloudEQ company the rows are imported under (R-113-3). Detected from the
 * sheet's heading: one line, with no control to change it (a detected entity
 * cannot be overridden). Not detected: what the sheet calls itself, then a
 * required choice, then the plan is asked for again.
 */
function EntityLine({
  plan,
  entityLabel,
  activeEntities,
  picked,
  disabled,
  onPick,
  onPlan,
}: {
  readonly plan: PlanVendorImportResponse;
  readonly entityLabel: string;
  readonly activeEntities: readonly { readonly entity_key: string; readonly display_name: string }[];
  readonly picked: string;
  readonly disabled: boolean;
  readonly onPick: (key: string) => void;
  readonly onPlan: (key: string) => void;
}) {
  const entity = plan.entity;
  if (entity.detected_key !== null) {
    return (
      <p className="vi-entity" data-testid="vi-entity">
        Imported under <strong>{entityLabel}</strong> — from the sheet’s heading.
      </p>
    );
  }
  const value = picked !== '' ? picked : (entity.chosen_key ?? '');
  return (
    <div className="vi-entity" data-testid="vi-entity">
      {entity.preamble_text === null ? (
        <p>The sheet has no heading above its header row, so it names no cloudEQ company.</p>
      ) : (
        <p>
          The sheet’s heading reads “{entity.preamble_text.slice(0, VENDOR_IMPORT_PREAMBLE_TEXT_MAX)}”, which names no
          cloudEQ company CLM holds.
        </p>
      )}
      {entity.chosen_key === null ? (
        <p>Choose the company whose vendor list this is. The rows are read under it.</p>
      ) : (
        <p>
          Imported under <strong>{entityLabel}</strong> — chosen by you.
        </p>
      )}
      <div className="frow">
        <div className="field">
          <label htmlFor="vendor-import-entity">cloudEQ company</label>
          <select
            id="vendor-import-entity"
            required
            aria-required="true"
            value={value}
            disabled={disabled}
            onChange={(event) => onPick(event.target.value)}
          >
            <option value="">Choose…</option>
            {activeEntities.map((option) => (
              <option key={option.entity_key} value={option.entity_key}>
                {option.display_name}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className="btn"
          disabled={disabled || value === '' || value === entity.chosen_key}
          onClick={() => onPlan(value)}
        >
          {entity.chosen_key === null ? 'Read the sheet under this company' : 'Read the sheet again under this company'}
        </button>
      </div>
      {activeEntities.length === 0 ? <p className="mut">The list of cloudEQ companies has not loaded.</p> : null}
    </div>
  );
}
